import type { ReactNode } from 'react';

import { cn } from '../base/utils';

const imageExtPattern = /\.(png|jpe?g|webp|gif|svg|bmp|avif)$/i;
const audioExtPattern = /\.(wav|mp3|ogg|m4a|flac|aac)$/i;

export interface WorkbenchGalleryColumn {
  key: string;
  label?: ReactNode;
  renderType?: string;
  type?: string;
  wrap?: boolean;
  [key: string]: any;
}

const isLikelyUrl = (value: unknown) => /^https?:\/\//i.test(String(value || ''));
const getColumnRenderKind = (column: WorkbenchGalleryColumn | undefined) => String(column?.renderType || column?.type || '').toLowerCase();
const isImageLikeValue = (value: unknown) => String(value || '').startsWith('data:image/') || imageExtPattern.test(String(value || ''));
const isAudioLikeValue = (value: unknown) => String(value || '').startsWith('data:audio/') || audioExtPattern.test(String(value || ''));
const escapeSvgText = (value: unknown) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

export const getDefaultGalleryFieldValue = (column?: WorkbenchGalleryColumn, row?: any) => {
  if (!column) return null;
  return row?.[column.key] ?? null;
};

export const getDefaultGalleryDisplayText = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    const parts = value.map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return '';
      const objectItem = item as Record<string, any>;
      return objectItem.text || objectItem.label || objectItem.title || objectItem.display || objectItem.value || objectItem.recordId || '';
    }).filter(Boolean);
    return parts.join(' · ') || '—';
  }
  if (typeof value === 'object') {
    const objectValue = value as Record<string, any>;
    return String(objectValue.text || objectValue.label || objectValue.title || objectValue.display || objectValue.value || '—');
  }
  return String(value);
};

export const isDefaultGalleryImageField = (column: WorkbenchGalleryColumn = { key: '' }, value: unknown) => (
  getColumnRenderKind(column) === 'image' || isImageLikeValue(value)
);

export const isDefaultGalleryAudioField = (column: WorkbenchGalleryColumn = { key: '' }, value: unknown) => (
  getColumnRenderKind(column) === 'audio' || isAudioLikeValue(value)
);

export const getDefaultGalleryImagePreviewSrc = (value: unknown, rowId?: string | number, columnLabel?: ReactNode) => {
  if (isLikelyUrl(value) || String(value || '').startsWith('data:image/')) return String(value);
  const title = escapeSvgText(value || columnLabel || rowId || '');
  const titleText = title
    ? `<text x='68' y='450' fill='white' font-family='Inter, Arial' font-size='52' font-weight='700'>${title}</text>`
    : '';
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 960 540'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#0f172a'/><stop offset='55%' stop-color='#1d4ed8'/><stop offset='100%' stop-color='#14b8a6'/></linearGradient></defs><rect width='960' height='540' fill='url(#g)'/><g opacity='0.28'><circle cx='780' cy='130' r='150' fill='#67e8f9'/><circle cx='180' cy='430' r='170' fill='#a78bfa'/></g>${titleText}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const isDefaultGalleryMediaColumn = (
  column: WorkbenchGalleryColumn | undefined,
  row: any,
  {
    getFieldValue = getDefaultGalleryFieldValue,
    shouldEnhanceMultimodal = true,
    isImageField = isDefaultGalleryImageField,
    isAudioField = isDefaultGalleryAudioField,
  }: {
    getFieldValue?: (column: WorkbenchGalleryColumn, row: any) => unknown;
    shouldEnhanceMultimodal?: boolean;
    isImageField?: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
    isAudioField?: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
  } = {},
) => {
  if (!column || !shouldEnhanceMultimodal) return false;
  const value = getFieldValue(column, row);
  return isImageField(column, value) || isAudioField(column, value);
};

export const isDefaultGalleryTitleCandidate = (
  column: WorkbenchGalleryColumn | undefined,
  row: any,
  { isMediaColumn = () => false }: { isMediaColumn?: (column: WorkbenchGalleryColumn, row: any) => boolean } = {},
) => {
  if (!column || column.renderType === 'icon') return false;
  return !isMediaColumn(column, row);
};

export const isDefaultGallerySummaryCandidate = (
  column: WorkbenchGalleryColumn | undefined,
  row: any,
  { isMediaColumn = () => false }: { isMediaColumn?: (column: WorkbenchGalleryColumn, row: any) => boolean } = {},
) => {
  if (!column || column.renderType === 'icon') return false;
  return !isMediaColumn(column, row);
};

export const renderDefaultGalleryFieldValue = (
  column: WorkbenchGalleryColumn,
  row: any,
  {
    compact = false,
    getFieldValue = getDefaultGalleryFieldValue,
    getDisplayText = getDefaultGalleryDisplayText,
  }: {
    compact?: boolean;
    getFieldValue?: (column: WorkbenchGalleryColumn, row: any) => unknown;
    getDisplayText?: (value: unknown) => ReactNode;
  } = {},
) => {
  const value = getFieldValue(column, row);
  return (
    <span className={cn('leading-relaxed', compact && column?.wrap === true && 'line-clamp-2 block')}>
      {getDisplayText(value)}
    </span>
  );
};
